import {
  Accordion as AccordionPrimitive,
  type AccordionItemProps,
  type AccordionPanelProps,
  type AccordionRootProps,
  type AccordionTriggerProps,
} from '@base-ui/react/accordion';
import { cn } from '@foundation/utils/cn';
import { createContext, useContext } from 'react';

export type HeadlessAccordionStyles = {
  readonly item?: string;
  readonly trigger?: string;
  readonly panel?: string;
};

export type HeadlessAccordionProps<Value = unknown> =
  AccordionRootProps<Value> & {
    readonly styles?: HeadlessAccordionStyles;
  };

type HeadlessAccordionContextValue = {
  readonly styles: HeadlessAccordionStyles;
};

const HeadlessAccordionContext =
  createContext<HeadlessAccordionContextValue | null>(null);

function useHeadlessAccordionContext(
  component: string,
): HeadlessAccordionContextValue {
  const context = useContext(HeadlessAccordionContext);
  if (!context) {
    throw new Error(`${component} must be used within HeadlessAccordion.Root`);
  }
  return context;
}

function Root<Value>({
  className,
  styles = {},
  keepMounted = true,
  ...props
}: HeadlessAccordionProps<Value>) {
  return (
    <HeadlessAccordionContext.Provider value={{ styles }}>
      <AccordionPrimitive.Root
        className={cn('flex w-full flex-col', className)}
        keepMounted={keepMounted}
        {...props}
      />
    </HeadlessAccordionContext.Provider>
  );
}

export type HeadlessAccordionItemProps = AccordionItemProps;

function Item({ className, ...props }: HeadlessAccordionItemProps) {
  const { styles } = useHeadlessAccordionContext('HeadlessAccordion.Item');
  return (
    <AccordionPrimitive.Item
      className={cn(styles.item, className)}
      {...props}
    />
  );
}

export type HeadlessAccordionTriggerProps = AccordionTriggerProps;

// Header is a structural-only wrapper required by Base UI, it carries no styling
function Trigger({ className, ...props }: HeadlessAccordionTriggerProps) {
  const { styles } = useHeadlessAccordionContext('HeadlessAccordion.Trigger');
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={cn(
          'group flex flex-1 items-center justify-between outline-none',
          styles.trigger,
          className,
        )}
        {...props}
      />
    </AccordionPrimitive.Header>
  );
}

export type HeadlessAccordionPanelProps = AccordionPanelProps;

// grid-rows 0fr->1fr animates far smoother than the height/--accordion-panel-height
// var approach (no JS-measured pixel height, no forced layout per frame); overflow-hidden
// on the inner wrapper is required for the 0fr row to actually collapse to zero
function Panel({ className, children, ...props }: HeadlessAccordionPanelProps) {
  const { styles } = useHeadlessAccordionContext('HeadlessAccordion.Panel');
  return (
    <AccordionPrimitive.Panel
      className={cn(
        'group grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-in-out data-open:grid-rows-[1fr] data-open:data-starting-style:grid-rows-[0fr]',
        styles.panel,
        className,
      )}
      {...props}
    >
      <div className="overflow-hidden">{children}</div>
    </AccordionPrimitive.Panel>
  );
}

export const HeadlessAccordion = {
  Root,
  Item,
  Trigger,
  Panel,
};
